/**
 * 搭配推荐：Swiper 横向轮播（依赖主题已引入的 Swiper 7）
 */
if (!customElements.get('pairs-well-with-slider')) {
  class PairsWellWithSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this._retryTimer = null;
      this._progressTrack = null;
      this._scrubPointerId = null;
      this._onProgressPointerDown = this._onProgressPointerDown.bind(this);
      this._onProgressPointerMove = this._onProgressPointerMove.bind(this);
      this._onProgressPointerUp = this._onProgressPointerUp.bind(this);
    }

    connectedCallback() {
      this.initWhenReady();
    }

    disconnectedCallback() {
      if (this._retryTimer) {
        clearTimeout(this._retryTimer);
        this._retryTimer = null;
      }
      this.destroy();
    }

    /** 等待全局 Swiper 后再初始化 */
    initWhenReady() {
      if (typeof Swiper === 'undefined') {
        this._retryTimer = setTimeout(() => this.initWhenReady(), 50);
        return;
      }
      this._retryTimer = null;
      this.init();
    }

    init() {
      const el = this.querySelector('.pairs-well-with__swiper');
      if (!el) return;

      this.destroy();

      this.swiper = new Swiper(el, {
        slidesPerView: 3,
        /* 与 pairs-well-with.css 中 --pairs-well-with-gap 保持一致 */
        spaceBetween: 10,
        watchOverflow: true,
        speed: 450,
        on: {
          progress: () => this.updateProgressBar(),
          setTranslate: () => this.updateProgressBar(),
          resize: () => this.updateProgressBar(),
          slideChange: () => this.updateProgressBar(),
        },
      });

      /* 须在 new Swiper 返回后调用，on.init 时 this.swiper 尚未赋值 */
      this.updateProgressBar();
      this._setupProgressScrub();
    }

    /** 与进度条、拖拽共用：是否锁死、拇指宽度、可移动区间 */
    _getProgressMetrics() {
      const swiper = this.swiper;
      if (!swiper || !swiper.slides) return null;
      const total = swiper.slides.length;
      if (total < 1) return null;

      let slidesPerView = 3;
      if (typeof swiper.slidesPerViewDynamic === 'function') {
        slidesPerView = swiper.slidesPerViewDynamic();
      } else if (typeof swiper.params.slidesPerView === 'number') {
        slidesPerView = swiper.params.slidesPerView;
      }

      const locked = swiper.isLocked === true || total <= slidesPerView;
      const thumbPct = locked ? 100 : Math.min(100, (slidesPerView / total) * 100);
      const maxLeft = Math.max(0, 100 - thumbPct);

      return { total, slidesPerView, locked, thumbPct, maxLeft };
    }

    /** 底部进度条：指示块宽度 ≈ 可见比例，位置随 swiper.progress 移动 */
    updateProgressBar() {
      const indicator = this.querySelector('.pairs-well-with__progress-indicator');
      const track = this.querySelector('.pairs-well-with__progress');
      if (!indicator || !track || !this.swiper) return;

      const metrics = this._getProgressMetrics();
      if (!metrics) {
        track.hidden = true;
        return;
      }

      const { locked, thumbPct, maxLeft } = metrics;
      const swiper = this.swiper;
      const progress = typeof swiper.progress === 'number' && !Number.isNaN(swiper.progress) ? swiper.progress : 0;
      const leftPct = locked ? 0 : maxLeft * progress;

      indicator.style.width = `${thumbPct}%`;
      indicator.style.left = `${leftPct}%`;
      track.hidden = false;
    }

    _setupProgressScrub() {
      this._teardownProgressScrub();
      const track = this.querySelector('.pairs-well-with__progress');
      if (!track) return;
      this._progressTrack = track;
      track.addEventListener('pointerdown', this._onProgressPointerDown);
    }

    _teardownProgressScrub() {
      const track = this._progressTrack;
      if (!track) return;
      track.removeEventListener('pointerdown', this._onProgressPointerDown);
      track.removeEventListener('pointermove', this._onProgressPointerMove);
      track.removeEventListener('pointerup', this._onProgressPointerUp);
      track.removeEventListener('pointercancel', this._onProgressPointerUp);
      if (this._scrubPointerId != null) {
        try {
          track.releasePointerCapture(this._scrubPointerId);
        } catch (_) {
          /* 可能已释放 */
        }
        this._scrubPointerId = null;
      }
      this._progressTrack = null;
    }

    _onProgressPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      const swiper = this.swiper;
      const track = this._progressTrack;
      if (!swiper || !track) return;

      const metrics = this._getProgressMetrics();
      if (!metrics || metrics.locked) return;

      e.preventDefault();
      this._scrubPointerId = e.pointerId;
      track.setPointerCapture(e.pointerId);
      track.addEventListener('pointermove', this._onProgressPointerMove);
      track.addEventListener('pointerup', this._onProgressPointerUp);
      track.addEventListener('pointercancel', this._onProgressPointerUp);
      swiper.setTransition(0);
      this._applyScrubFromClientX(e.clientX);
    }

    _onProgressPointerMove(e) {
      if (e.pointerId !== this._scrubPointerId) return;
      e.preventDefault();
      this._applyScrubFromClientX(e.clientX);
    }

    _onProgressPointerUp(e) {
      if (e.pointerId !== this._scrubPointerId) return;
      const track = this._progressTrack;
      const swiper = this.swiper;
      if (track) {
        track.removeEventListener('pointermove', this._onProgressPointerMove);
        track.removeEventListener('pointerup', this._onProgressPointerUp);
        track.removeEventListener('pointercancel', this._onProgressPointerUp);
        try {
          track.releasePointerCapture(e.pointerId);
        } catch (_) {}
      }
      this._scrubPointerId = null;

      if (swiper) {
        const speed = typeof swiper.params.speed === 'number' ? swiper.params.speed : 450;
        swiper.setTransition(speed);
        if (typeof swiper.slideToClosest === 'function') {
          swiper.slideToClosest(speed);
        } else {
          const metrics = this._getProgressMetrics();
          if (metrics && !metrics.locked) {
            const maxIndex = Math.max(0, metrics.total - Math.ceil(metrics.slidesPerView));
            const ratio = this._clientXToProgressRatio(e.clientX);
            const idx = Math.min(maxIndex, Math.max(0, Math.round(ratio * maxIndex)));
            swiper.slideTo(idx, speed);
          }
        }
      }
      this.updateProgressBar();
    }

    _clientXToProgressRatio(clientX) {
      const track = this._progressTrack;
      if (!track) return 0;
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return 0;
      let ratio = (clientX - rect.left) / rect.width;
      if (ratio < 0) ratio = 0;
      else if (ratio > 1) ratio = 1;
      return ratio;
    }

    /** 将轨道水平位置映射为 Swiper 0~1 进度并 setTranslate */
    _applyScrubFromClientX(clientX) {
      const swiper = this.swiper;
      if (!swiper) return;
      const ratio = this._clientXToProgressRatio(clientX);
      const minT = swiper.minTranslate();
      const maxT = swiper.maxTranslate();
      const translate = minT + (maxT - minT) * ratio;
      swiper.setTranslate(translate);
      swiper.updateProgress();
      swiper.updateActiveIndex();
      swiper.updateSlidesClasses();
      this.updateProgressBar();
    }

    destroy() {
      this._teardownProgressScrub();
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  customElements.define('pairs-well-with-slider', PairsWellWithSlider);
}
